import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { HTMLAttributes, useCallback, useEffect, useRef, useState } from "react";
import { useGetIdentity, useRedirect } from "react-admin";
import { useHotkeys } from "react-hotkeys-hook";
import {
  type DrivingSchoolSearchResultDto,
  getAuthenticatedServerClient,
  type GuardianSearchResultDto,
  type InstructorSearchResultDto,
  type StudentSearchResultDto,
} from "../api/server.api.js";
import { assertNever } from "../backoffice.utils.js";
import { useDebouncedEffect } from "../hooks/useDebouncedEffect.js";
import { DrivingSchoolSearchIcon } from "../icons/DrivingSchoolSearchIcon";
import { InstructorSearchIcon } from "../icons/InstructorSearchIcon";
import { StudentSearchIcon } from "../icons/StudentSearchIcon";
import { Delayed } from "./Delayed.js";
import { Column } from "./Column.js";
import { Row } from "./Row.js";
import { useMarkUiRenderedRef } from "../trackStartupPerformance";
import {
  addRecentSearch,
  clearRecentSearches,
  getRecentSearches,
  type GlobalSearchOption,
  removeRecentSearch,
} from "./globalSearchHistory.js";

export default function GlobalSearchBox(props: {
  autoFocus?: boolean;
  onSelect?: () => void;
  variant?: "default" | "mobile";
}) {
  const { autoFocus, onSelect, variant = "default" } = props;
  const isMobile = variant === "mobile";
  const markUiRenderedRef = useMarkUiRenderedRef<HTMLDivElement>();
  const { open, setOpen, value, setValue, inputValue, setInputValue, loading, options } = useAutocompleteSearch();
  const { identity } = useGetIdentity();
  const userId = identity ? String(identity.id) : undefined;
  const [recentSearchHistory, setRecentSearchHistory] = useState<{
    userId?: string;
    searches: GlobalSearchOption[];
  }>({ searches: [] });
  const [highlightedOption, setHighlightedOption] = useState<GlobalSearchOption | null>(null);
  const redirect = useRedirect();
  const recentSearches = recentSearchHistory.userId === userId ? recentSearchHistory.searches : [];
  const showRecentSearches = !inputValue && recentSearches.length > 0;
  const displayedOptions = showRecentSearches ? recentSearches : options;

  useEffect(() => {
    setRecentSearchHistory({ userId, searches: userId ? getRecentSearches(userId) : [] });
  }, [userId]);

  const resetInputValue = () => {
    setInputValue("");
  };

  const handleRemoveRecentSearch = (option: GlobalSearchOption) => {
    if (userId) {
      setRecentSearchHistory({ userId, searches: removeRecentSearch(userId, option) });
      setHighlightedOption(null);
    }
  };

  const handleClearRecentSearches = () => {
    if (userId) {
      clearRecentSearches(userId);
      setRecentSearchHistory({ userId, searches: [] });
      setHighlightedOption(null);
    }
  };

  return (
    <Autocomplete
      ref={markUiRenderedRef}
      sx={{
        borderRadius: isMobile ? "20px" : "21px",
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        "&:hover": {
          borderColor: "text.secondary",
        },
        width: isMobile ? "100%" : undefined,
        maxWidth: isMobile ? "100%" : undefined,
        minWidth: 0,
        "& .MuiInputBase-root": {
          borderRadius: "20px !important",
          height: isMobile ? undefined : "40px",
          minWidth: 0,
          paddingLeft: isMobile ? "12px !important" : "20px !important",
        },
      }}
      blurOnSelect
      autoHighlight
      clearOnEscape
      openOnFocus
      value={value}
      inputValue={inputValue}
      componentsProps={{
        paper: {
          id: "global-search-results",
        },
      }}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onHighlightChange={(_, option) => setHighlightedOption(option)}
      onKeyDown={(event) => {
        if (event.key === "Delete" && event.shiftKey && open && showRecentSearches) {
          event.preventDefault();
          handleClearRecentSearches();
        } else if (event.key === "Delete" && open && showRecentSearches && highlightedOption) {
          event.preventDefault();
          handleRemoveRecentSearch(highlightedOption);
        }
      }}
      open={open && (inputValue !== "" || recentSearches.length > 0)}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      filterOptions={(options, _) => options} // Disable the UI filtering here, we do it on the server
      isOptionEqualToValue={(option, value) => option.resource === value.resource && option.id === value.id}
      getOptionLabel={(option) => option.name}
      options={displayedOptions}
      loading={!showRecentSearches && loading}
      loadingText="Lädt..."
      noOptionsText="Keine Ergebnisse"
      openText="Öffnen"
      clearText="Löschen"
      closeText="Schließen"
      groupBy={(option) => (showRecentSearches ? "Letzte Suchen" : option.group)}
      onChange={(_, value, reason) => {
        if (value && reason === "selectOption") {
          if (userId) {
            setRecentSearchHistory({ userId, searches: addRecentSearch(userId, value) });
          }
          redirect("edit", value.resource, value.id);
          setValue(null);
          resetInputValue();
          onSelect?.();
        } else {
          setValue(value);
        }
      }}
      renderOption={(props, option, state) =>
        renderSearchResultOption(props, option, state, showRecentSearches ? handleRemoveRecentSearch : undefined)
      }
      renderInput={(params) => (
        <SearchInput
          {...params}
          autoFocus={autoFocus}
          loading={loading}
          resetValue={resetInputValue}
          canClearRecentSearches={open && showRecentSearches}
          variant={variant}
        />
      )}
      renderGroup={(params) => (
        <SearchResultGroup {...params} onClear={showRecentSearches ? handleClearRecentSearches : undefined} />
      )}
    />
  );
}

function renderSearchResultOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: GlobalSearchOption,
  { inputValue }: AutocompleteRenderOptionState,
  onRemove?: (option: GlobalSearchOption) => void,
) {
  const { id, resource, name, subTextDetails, group } = option;

  // Render the results with match highlighting
  // The highlighted parts are shown in regular font weight, the rest in bold
  const nameMatches = match(name, inputValue, { insideWords: true });
  const nameParts = parse(name, nameMatches);

  const subText = subTextDetails?.text ?? "";
  const subTextMatches = subText ? match(subText, inputValue, { insideWords: true }) : [];
  const subTextParts = parse(subText, subTextMatches);

  const icon =
    group === "Fahrschulen" ? (
      <DrivingSchoolSearchIcon />
    ) : group === "Fahrlehrer" ? (
      <InstructorSearchIcon />
    ) : group === "Fahrschüler" || group === "Gesetzliche Vertreter" ? (
      <StudentSearchIcon />
    ) : null;

  return (
    <Box component="li" {...props} sx={{ px: "16px" }} key={`${resource}-${id}`}>
      <Stack direction="row" style={{ display: "flex", alignItems: "start", gap: "10px", width: "100%" }}>
        <Box sx={{ pt: "2px" }}>{icon}</Box>
        <Column width={onRemove ? "calc(100% - 70px)" : "calc(100% - 28px)"}>
          {" "}
          {/* 100% parent width - (icon width + gap width) to apply ellipsis on relatedResource */}
          <Row>
            {nameParts.map((part, index) => (
              <StringChunk key={index + part.text} text={part.text} highlight={part.highlight} />
            ))}
          </Row>
          {subTextDetails && (
            <Row sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {subTextParts.map((part, index) => (
                <StringChunk
                  key={index + part.text}
                  text={part.text}
                  type="secondary"
                  highlight={part.highlight}
                  sx={{ wordBreak: "keep-all" }}
                />
              ))}
              {subTextDetails.relatedResource && (
                <StringChunk text={subTextDetails.relatedResource} type="secondary" highlight sx={{ ml: "4px" }} />
              )}
            </Row>
          )}
        </Column>
        {onRemove && (
          <IconButton
            size="small"
            aria-label={`${name} aus den letzten Suchen entfernen`}
            title="Aus den letzten Suchen entfernen"
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove(option);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}

interface StringChunkProps {
  text: string;
  type?: "primary" | "secondary";
  highlight: boolean;
  sx?: SxProps;
}

function StringChunk({ text, type = "primary", highlight, sx = {} }: StringChunkProps) {
  const typeStyles = {
    primary: {
      fontSize: 15,
      color: "text.primary",
    },
    secondary: {
      fontSize: 11,
      color: "text.secondary",
    },
  };

  return (
    <Box
      component="span"
      sx={{
        fontWeight: type === "secondary" || highlight ? 400 : 700,
        ...typeStyles[type],
        ...sx,
      }}
    >
      {replaceSpacesWithNbsp(text)}
    </Box>
  );
}

export function replaceSpacesWithNbsp(text: string) {
  return text.replaceAll(" ", "\u00A0");
}

function SearchResultGroup(props: AutocompleteRenderGroupParams & { onClear?: () => void }) {
  const { children, group, onClear } = props;
  if (!Array.isArray(children) || children.length === 0) return null;
  return (
    <>
      <Box key={`${group}-group-header`} component="li" sx={{ p: "8px 12px 3px 20px" }}>
        <Row sx={{ minHeight: 30, alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 12, fontWeight: "normal", color: "text.secondary" }}>{group}</Typography>
          {onClear && (
            <Button
              size="small"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onClear();
              }}
            >
              Alle löschen
            </Button>
          )}
        </Row>
      </Box>
      {children}
      {/* Extra space after the group options */}
      <Box key={`${group}-hidden-divider`} component="li" sx={{ height: "8px" }} />
    </>
  );
}

function SearchInput(
  props: AutocompleteRenderInputParams & {
    autoFocus?: boolean;
    loading: boolean;
    resetValue: () => void;
    canClearRecentSearches: boolean;
    variant?: "default" | "mobile";
  },
) {
  const { autoFocus, loading, resetValue, canClearRecentSearches, variant = "default", ...params } = props;
  const isMobile = variant === "mobile";
  const inputRef = useRef<HTMLInputElement>(null);
  const userAgent = navigator.userAgent;
  const isMacOS = /Mac OS X/.test(userAgent);
  useHotkeys("ctrl+f", () => inputRef.current?.focus(), { preventDefault: true });
  useHotkeys("esc", () => inputRef.current?.blur(), { enableOnFormTags: true });
  useEffect(() => {
    if (!autoFocus) return;
    inputRef.current?.focus();
  }, [autoFocus]);
  const searchFieldPlaceholder = (
    <InputAdornment position="start" sx={{ color: "text.secondary" }}>
      <Typography sx={{ fontSize: 14 }}>Suche</Typography>
      <Chip
        sx={{
          backgroundColor: "action.hover",
          color: "text.disabled",
          marginLeft: 1,
          fontWeight: "bold",
          fontSize: 10,
          height: 23,
        }}
        label={isMacOS ? "CTRL + F" : "STRG + F"}
      />
    </InputAdornment>
  );
  const [placeholder, setPlaceholder] = useState<JSX.Element | string>(searchFieldPlaceholder);

  const handleFocus = () => {
    setPlaceholder("");
  };

  const handleBlur = () => {
    setPlaceholder(searchFieldPlaceholder);
  };

  return (
    <TextField
      {...params}
      sx={{
        width: "100%",
        minWidth: 0,
        "& .MuiInputBase-input::placeholder": {
          color: "transparent",
        },
        "& .MuiInputBase-input": {
          minWidth: 0,
        },
      }}
      label=""
      placeholder="Suche"
      inputProps={{
        ...params.inputProps,
        "aria-keyshortcuts": canClearRecentSearches ? "Delete Shift+Delete" : undefined,
      }}
      inputRef={inputRef}
      autoFocus={autoFocus}
      onFocus={handleFocus}
      onBlur={handleBlur}
      size="small"
      variant="outlined"
      margin="none"
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            {placeholder}
          </Box>
        ),
        endAdornment: (
          <>
            {props.inputProps.value && (
              <>
                <Delayed ms={500}>
                  <InputAdornment position="end">{loading && <CircularProgress size={20} />}</InputAdornment>
                </Delayed>
                <InputAdornment position="end">
                  <IconButton
                    sx={{
                      height: 32,
                      width: 32,
                      marginRight: isMobile ? 0 : -40,
                    }}
                    onClick={resetValue}
                  >
                    <CloseIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                  </IconButton>
                </InputAdornment>
              </>
            )}
          </>
        ),
      }}
    />
  );
}

function useAutocompleteSearch(): {
  open: boolean;
  setOpen: (open: boolean) => void;
  value: GlobalSearchOption | null;
  setValue: (value: GlobalSearchOption | null) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  loading: boolean;
  options: GlobalSearchOption[];
} {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<GlobalSearchOption | null>(null);
  const [inputValue, setInputValue] = useState("");

  const [options, setOptions] = useState<GlobalSearchOption[]>([]);
  const [loading, setLoading] = useState(true);

  const currentRequestRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!inputValue) {
      return;
    }
    const requestId = ++currentRequestRef.current;
    setLoading(true);
    try {
      const serverClient = await getAuthenticatedServerClient();
      const query = inputValue.trim();
      if (query && query.length > 1) {
        const { students, instructors, drivingSchools, guardians } = await serverClient.search({
          queries: { query },
        });
        if (requestId === currentRequestRef.current) {
          setOptions([
            ...(students ?? []).map((dto) => _serverResultToOption("students", dto)),
            ...(instructors ?? []).map((dto) => _serverResultToOption("instructors", dto)),
            ...(drivingSchools ?? []).map((dto) => _serverResultToOption("drivingSchools", dto)),
            ...(guardians ?? []).map((dto) => _serverResultToOption("guardians", dto)),
          ]);
        }
      } else {
        setOptions([]);
      }
    } finally {
      if (requestId === currentRequestRef.current) {
        setLoading(false);
      }
    }
  }, [inputValue]);

  useDebouncedEffect(fetchData, 150, [inputValue]);

  return { open, setOpen, value, setValue, inputValue, setInputValue, loading, options };
}

function _serverResultToOption(group: "students", dto: StudentSearchResultDto): GlobalSearchOption;
function _serverResultToOption(group: "instructors", dto: InstructorSearchResultDto): GlobalSearchOption;
function _serverResultToOption(group: "drivingSchools", dto: DrivingSchoolSearchResultDto): GlobalSearchOption;
function _serverResultToOption(group: "guardians", dto: GuardianSearchResultDto): GlobalSearchOption;
function _serverResultToOption(
  group: "students" | "instructors" | "drivingSchools" | "guardians",
  dto: StudentSearchResultDto | InstructorSearchResultDto | DrivingSchoolSearchResultDto | GuardianSearchResultDto,
): GlobalSearchOption {
  const { id, uiString, relatedSearchResultUiString, relatedSearchFieldUiString } = dto;
  type CommonFieldsKeys = "id" | "name" | "subTextDetails";
  const commonValues: Pick<GlobalSearchOption, CommonFieldsKeys> = { id, name: uiString };
  if (relatedSearchResultUiString) {
    commonValues.subTextDetails = {
      text: relatedSearchResultUiString,
      relatedResource: relatedSearchFieldUiString,
    };
  }
  if (group === "students") {
    return { ...commonValues, resource: "students", group: "Fahrschüler" };
  } else if (group === "instructors") {
    return { ...commonValues, resource: "instructors", name: dto.uiString, group: "Fahrlehrer" };
  } else if (group === "drivingSchools") {
    return { ...commonValues, resource: "drivingSchools", name: dto.uiString, group: "Fahrschulen" };
  } else if (group === "guardians") {
    const { studentId } = dto as GuardianSearchResultDto;
    return { ...commonValues, id: studentId, resource: "students", group: "Gesetzliche Vertreter" };
  } else {
    assertNever(group);
  }
}
